import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ErrorDeApi, pedir } from '../api/cliente';
import { Boton, Encabezado, Tarjeta } from '../componentes/ui/basicos';
import { Campo } from '../componentes/ui/formulario';
import { useTextoDeError } from '../componentes/ui/estados';
import { usePermiso } from '../sesion/ProveedorDeSesion';
import { BuscadorDeArticulos } from '../venta/PiezasDeVenta';

interface VentaEncontrada {
  readonly id: string;
  readonly numero: string;
  readonly estado: string;
  readonly emitidoEn: string;
  readonly total: string;
  readonly nombreDelCliente: string | null;
}

interface LineaDevolvible {
  readonly lineaId: string;
  readonly descripcion: string;
  readonly codigo: string;
  readonly cantidad: string;
  readonly devuelta: string;
  readonly disponible: string;
  readonly precioUnitario: string;
}

function conDosDecimales(importe: string): string {
  const [entera, fraccion = ''] = importe.split('.');
  return `${entera ?? '0'}.${fraccion.padEnd(2, '0').slice(0, 2)}`;
}

/** Las cantidades llegan con tres decimales; en pantalla sobran los ceros del final. */
function cantidadVisible(cantidad: string): string {
  return cantidad.includes('.') ? cantidad.replace(/\.?0+$/, '') : cantidad;
}

/**
 * Devoluciones: el cliente trajo mercadería.
 *
 * Se busca la venta **por el número que está impreso en el papel**, que es lo único que el
 * cliente trae. Después se elige qué vuelve y cuánto, línea por línea, con lo que ya se devolvió
 * a la vista: sin eso, la segunda devolución de la misma venta se hace a ciegas.
 *
 * Esta pantalla resuelve además el hueco que dejó la anulación: hasta ahora, una venta que no
 * fuera la última no se podía encontrar desde ningún lado.
 */
export function Devoluciones(): React.ReactElement {
  const { t } = useTranslation();
  const clienteDeConsultas = useQueryClient();
  const textoDeError = useTextoDeError();

  const puedeAutorizar = usePermiso('VENTAS.DEVOLUCION.AUTORIZAR_PLAZO');

  const [numero, setNumero] = useState('');
  const [buscado, setBuscado] = useState('');
  const [venta, setVenta] = useState<VentaEncontrada | null>(null);
  const [cantidades, setCantidades] = useState<Record<string, string>>({});
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [hecha, setHecha] = useState<string | null>(null);
  const [fueraDePlazo, setFueraDePlazo] = useState(false);
  // Lo que el cliente se lleva a cambio. Vacío es una devolución a secas.
  const [seLleva, setSeLleva] = useState<
    { varianteId: string; codigo: string; nombre: string; precio: string }[]
  >([]);
  const [efectivo, setEfectivo] = useState('');
  // El puesto de caja donde sale el efectivo. Se toma el primero: una devolución se hace en el
  // mostrador donde se está parado, y elegirlo sería un paso más para el caso de siempre.
  const puestos = useQuery({
    queryKey: ['puestos-de-caja'],
    queryFn: () => pedir<{ id: string }[]>('/puestos-de-caja'),
  });
  const puestoActivo = puestos.data?.[0]?.id ?? '';

  const puntosDeVenta = useQuery({
    queryKey: ['puntos-de-venta'],
    queryFn: () => pedir<{ id: string }[]>('/puntos-de-venta'),
  });
  const puntoDeVentaActivo = puntosDeVenta.data?.[0]?.id ?? '';

  const encontradas = useQuery({
    queryKey: ['ventas-por-numero', buscado],
    enabled: buscado !== '',
    queryFn: () => pedir<VentaEncontrada[]>(`/ventas/buscar?numero=${encodeURIComponent(buscado)}`),
  });

  const devolvible = useQuery({
    queryKey: ['devolvible', venta?.id],
    enabled: venta !== null,
    queryFn: () => pedir<{ lineas: LineaDevolvible[] }>(`/ventas/${venta?.id ?? ''}/devolvible`),
  });

  const lineas = useMemo(
    () => (devolvible.data?.lineas ?? []).filter((linea) => Number(linea.disponible) > 0),
    [devolvible.data],
  );

  const devolver = useMutation({
    mutationFn: (autorizando: boolean) =>
      pedir<{
        numero: string;
        tipo: string;
        total: string;
        enEfectivo: string;
        aCuenta: string;
      }>('/devoluciones', {
        method: 'POST',
        headers: { 'Idempotency-Key': globalThis.crypto.randomUUID() },
        cuerpo: {
          comprobanteId: venta?.id ?? '',
          lineas: Object.entries(cantidades)
            .filter(([, cantidad]) => Number(cantidad) > 0)
            .map(([lineaId, cantidad]) => ({ lineaId, cantidad: cantidad.replace(',', '.') })),
          motivo: motivo.trim(),
          puestoDeCajaId: puestoActivo === '' ? undefined : puestoActivo,
          ...(autorizando ? { autorizarFueraDePlazo: true } : {}),
        },
      }),
    onSuccess: async (respuesta) => {
      // Se dice lo que se emitió. Una nota de crédito es un comprobante fiscal con su propia
      // numeración: llamarla "devolución" haría buscar un papel que no existe.
      setHecha(
        t(
          respuesta.tipo === 'nota_de_credito'
            ? 'devoluciones.hechaComoNota'
            : 'devoluciones.hecha',
          {
            numero: respuesta.numero,
            total: respuesta.total,
            enEfectivo: respuesta.enEfectivo,
            aCuenta: respuesta.aCuenta,
          },
        ),
      );
      setCantidades({});
      setMotivo('');
      setError(null);
      setFueraDePlazo(false);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['devolvible'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['existencias'] });
    },
    onError: (fallo: unknown) => {
      setFueraDePlazo(
        fallo instanceof ErrorDeApi && fallo.codigo === 'ERR_DEVOLUCION_FUERA_DE_PLAZO',
      );
      setError(textoDeError(fallo));
    },
  });

  const hayAlgoQueDevolver = Object.values(cantidades).some((cantidad) => Number(cantidad) > 0);

  // Los dos lados del cambio, para poder mostrar la diferencia antes de confirmar. El servidor
  // recalcula todo: esto es lo que ve quien atiende, no lo que decide el sistema.
  const valorDeLoQueVuelve = lineas.reduce((suma, linea) => {
    const cantidad = Number(cantidades[linea.lineaId] ?? '0');
    return suma + (Number.isNaN(cantidad) ? 0 : cantidad) * Number(linea.precioUnitario);
  }, 0);
  const valorDeLoQueSeLleva = seLleva.reduce((suma, articulo) => suma + Number(articulo.precio), 0);
  const diferencia = valorDeLoQueSeLleva - valorDeLoQueVuelve;

  const cambiar = useMutation({
    mutationFn: (autorizando: boolean) =>
      pedir<{ numeroDeDevolucion: string; numeroDeVenta: string; diferencia: string }>('/cambios', {
        method: 'POST',
        headers: { 'Idempotency-Key': globalThis.crypto.randomUUID() },
        cuerpo: {
          comprobanteId: venta?.id ?? '',
          lineasADevolver: Object.entries(cantidades)
            .filter(([, cantidad]) => Number(cantidad) > 0)
            .map(([lineaId, cantidad]) => ({ lineaId, cantidad: cantidad.replace(',', '.') })),
          lineasAVender: seLleva.map((articulo) => ({
            varianteId: articulo.varianteId,
            cantidad: '1',
          })),
          motivo: motivo.trim(),
          puntoDeVentaId: puntoDeVentaActivo,
          puestoDeCajaId: puestoActivo,
          ...(diferencia > 0
            ? { cobros: [{ medioDePago: 'efectivo', importe: efectivo.replace(',', '.') }] }
            : {}),
          ...(autorizando ? { autorizarFueraDePlazo: true } : {}),
        },
      }),
    onSuccess: async (respuesta) => {
      setHecha(
        t('devoluciones.cambioHecho', {
          devolucion: respuesta.numeroDeDevolucion,
          venta: respuesta.numeroDeVenta,
          diferencia: respuesta.diferencia,
        }),
      );
      setCantidades({});
      setSeLleva([]);
      setEfectivo('');
      setMotivo('');
      setError(null);
      setFueraDePlazo(false);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['devolvible'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['existencias'] });
    },
    onError: (fallo: unknown) => {
      setFueraDePlazo(
        fallo instanceof ErrorDeApi && fallo.codigo === 'ERR_DEVOLUCION_FUERA_DE_PLAZO',
      );
      setError(textoDeError(fallo));
    },
  });

  return (
    <>
      <Encabezado titulo={t('devoluciones.titulo')} descripcion={t('devoluciones.descripcion')} />

      <Tarjeta>
        <form
          className="flex max-w-md items-end gap-2"
          onSubmit={(evento) => {
            evento.preventDefault();
            setBuscado(numero.trim());
            setVenta(null);
            setHecha(null);
          }}
        >
          <Campo
            etiqueta={t('devoluciones.buscar')}
            inputMode="numeric"
            value={numero}
            ayuda={t('devoluciones.buscarAyuda')}
            onChange={(evento) => {
              setNumero(evento.target.value);
            }}
          />
          <Boton tono="principal" type="submit">
            {t('acciones.buscar')}
          </Boton>
        </form>

        {encontradas.isSuccess && encontradas.data.length === 0 ? (
          <p className="mt-3 text-sm text-texto-tenue">{t('devoluciones.sinResultados')}</p>
        ) : null}

        {encontradas.isSuccess && venta === null ? (
          <ul className="mt-3 flex flex-col gap-2">
            {encontradas.data.map((fila) => (
              <li
                key={fila.id}
                className="flex items-center justify-between gap-3 rounded-marco border border-borde px-3 py-2 text-sm"
              >
                <span>
                  {t('devoluciones.venta', { numero: fila.numero })}
                  <span className="block text-xs text-texto-tenue">
                    {new Date(fila.emitidoEn).toLocaleString()} ·{' '}
                    {fila.nombreDelCliente ?? t('devoluciones.consumidorFinal')}
                  </span>
                </span>
                <span className="numero">{conDosDecimales(fila.total)}</span>
                <Boton
                  tono="secundario"
                  onClick={() => {
                    setVenta(fila);
                    setError(null);
                    setHecha(null);
                  }}
                >
                  {t('devoluciones.elegir')}
                </Boton>
              </li>
            ))}
          </ul>
        ) : null}
      </Tarjeta>

      {venta === null ? null : (
        <Tarjeta titulo={t('devoluciones.venta', { numero: venta.numero })}>
          {devolvible.isSuccess && lineas.length === 0 ? (
            <p className="text-sm text-texto-tenue">{t('devoluciones.nadaQueDevolver')}</p>
          ) : null}

          {lineas.length > 0 ? (
            <>
              <ul className="flex flex-col divide-y divide-borde">
                {lineas.map((linea) => (
                  <li key={linea.lineaId} className="flex items-center gap-3 py-2 text-sm">
                    <span className="min-w-0 flex-1">
                      {linea.descripcion}
                      <span className="block text-xs text-texto-tenue">
                        <span className="numero">{linea.codigo}</span> · {t('devoluciones.vendida')}
                        : {cantidadVisible(linea.cantidad)} · {t('devoluciones.yaDevuelta')}:{' '}
                        {cantidadVisible(linea.devuelta)} · {t('devoluciones.disponible')}:{' '}
                        {cantidadVisible(linea.disponible)}
                      </span>
                    </span>
                    <input
                      aria-label={`${t('devoluciones.aDevolver')} ${linea.descripcion}`}
                      inputMode="decimal"
                      className="numero w-24 rounded-marco border border-borde bg-superficie px-2 py-1 text-right text-sm"
                      value={cantidades[linea.lineaId] ?? ''}
                      onChange={(evento) => {
                        setCantidades((previo) => ({
                          ...previo,
                          [linea.lineaId]: evento.target.value,
                        }));
                      }}
                    />
                  </li>
                ))}
              </ul>

              {/* El cambio vive acá y no en otra pantalla porque es lo mismo hasta la mitad:
                  buscar la venta y elegir qué vuelve. Lo que sigue —qué se lleva— es opcional. */}
              <div className="mt-4 border-t border-borde pt-3">
                <p className="mb-2 text-xs font-medium text-texto-tenue">
                  {t('devoluciones.loQueSeLleva')}
                </p>
                <BuscadorDeArticulos
                  listaDePreciosId={undefined}
                  etiqueta={t('devoluciones.buscarArticulo')}
                  autoFoco={false}
                  onElegir={(variante) => {
                    if (variante.precio === null) return;
                    setSeLleva((previos) => [
                      ...previos,
                      {
                        varianteId: variante.varianteId,
                        codigo: variante.codigo,
                        nombre: variante.nombre,
                        precio: variante.precio ?? '0',
                      },
                    ]);
                  }}
                />
                {seLleva.length > 0 ? (
                  <ul className="mt-2 flex flex-col gap-1 text-sm">
                    {seLleva.map((articulo, indice) => (
                      <li
                        key={`${articulo.varianteId}-${String(indice)}`}
                        className="flex items-center justify-between gap-2"
                      >
                        <span>{articulo.nombre}</span>
                        <span className="numero">{conDosDecimales(articulo.precio)}</span>
                        <Boton
                          tono="sutil"
                          onClick={() => {
                            setSeLleva((previos) => previos.filter((_, i) => i !== indice));
                          }}
                        >
                          {t('acciones.quitar')}
                        </Boton>
                      </li>
                    ))}
                  </ul>
                ) : null}

                {seLleva.length > 0 ? (
                  <p className="mt-2 text-sm">
                    {diferencia > 0
                      ? t('devoluciones.diferenciaAPagar')
                      : diferencia < 0
                        ? t('devoluciones.diferenciaADevolver')
                        : t('devoluciones.sinDiferencia')}
                    :{' '}
                    <span className="numero">{conDosDecimales(String(Math.abs(diferencia)))}</span>
                  </p>
                ) : null}

                {diferencia > 0 ? (
                  <div className="mt-2 max-w-xs">
                    <Campo
                      etiqueta={t('venta.efectivo')}
                      inputMode="decimal"
                      value={efectivo}
                      onChange={(evento) => {
                        setEfectivo(evento.target.value);
                      }}
                    />
                  </div>
                ) : null}
              </div>

              <div className="mt-3 flex flex-col gap-2">
                <Campo
                  etiqueta={t('devoluciones.motivo')}
                  obligatorio
                  maxLength={200}
                  value={motivo}
                  onChange={(evento) => {
                    setMotivo(evento.target.value);
                  }}
                />
                <div className="flex gap-2">
                  <Boton
                    tono="principal"
                    cargando={devolver.isPending || cambiar.isPending}
                    disabled={!hayAlgoQueDevolver || motivo.trim() === ''}
                    onClick={() => {
                      if (seLleva.length > 0) cambiar.mutate(false);
                      else devolver.mutate(false);
                    }}
                  >
                    {seLleva.length > 0
                      ? t('devoluciones.registrarCambio')
                      : t('devoluciones.registrar')}
                  </Boton>
                </div>
              </div>
            </>
          ) : null}

          {error === null ? null : (
            <p role="alert" className="mt-2 text-xs text-mal">
              {error}
            </p>
          )}

          {/* Igual que el límite de crédito: frenar sin ofrecer salida manda a alguien a buscar
              un encargado con el cliente esperando. */}
          {fueraDePlazo ? (
            puedeAutorizar ? (
              <Boton
                className="mt-2"
                tono="secundario"
                cargando={devolver.isPending}
                onClick={() => {
                  devolver.mutate(true);
                }}
              >
                {t('devoluciones.fueraDePlazo')}
              </Boton>
            ) : (
              <p className="mt-2 text-xs text-texto-tenue">{t('devoluciones.sinAutorizacion')}</p>
            )
          ) : null}

          {hecha === null ? null : (
            <p role="status" className="mt-2 text-sm text-bien">
              {hecha}
            </p>
          )}
        </Tarjeta>
      )}
    </>
  );
}
